import React, { useMemo, useState } from 'react'
import { DcChart, colors } from 'pixel-base'

import {
  ChartFilters,
  createChartOptions,
  createChartData,
  sortProductsData,
} from './topProductsUtils'
import { useDashboardProductsData } from './useDashboardData'

interface TopProductsChartProps {
  filters: ChartFilters
  height?: number | string
  setComponent: (_component: string) => void
  setShowDetail: (_showDetail: boolean) => void
  width?: string
  onDetailClick?: (data: any) => void
}

const TopProductsChart: React.FC<TopProductsChartProps> = ({
  filters,
  height = 350,
  setComponent,
  setShowDetail,
  onDetailClick,
}) => {
  const [sortBy, setSortBy] = useState<'revenue' | 'items_sold'>('revenue')

  // Use the shared hook instead of direct API call
  const { products: data, loading } = useDashboardProductsData(filters)

  // Calculate totals for display
  const totalRevenue = useMemo(() => {
    if (!data || !Array.isArray(data)) return 0
    return data.reduce((sum, item) => sum + (item.total_revenue || 0), 0)
  }, [data])

  const totalOrders = useMemo(() => {
    if (!data || !Array.isArray(data)) return 0
    return data.reduce((sum, item) => sum + (item.total_items_sold || 0), 0)
  }, [data])

  const sortedData = useMemo(() => {
    if (!data) return []

    const filteredData = data.filter(
      (item) => item.product_name && item.total_revenue !== undefined,
    )
    return sortProductsData(filteredData, sortBy).slice(0, 10) // Show top 10 products
  }, [data, sortBy])

  // Use shared chart data creation (show top 10)
  const chartData = useMemo(
    () => createChartData(sortedData, sortBy, 10),
    [sortedData, sortBy],
  )

  // Use shared chart options
  const chartOptions = createChartOptions(sortedData)

  // Handle sort by change
  const handleSortByChange = (newSortBy: 'revenue' | 'items_sold') => {
    setSortBy(newSortBy)
  }

  return (
    <div>
      {/* Chart */}
      <DcChart
        type="horizontalBar"
        data={chartData}
        options={chartOptions}
        height={350}
        width="100%"
        title="Top Products"
        isLoading={loading}
        toggleData={[
          {
            title: 'Revenue',
            data: `PKR ${totalRevenue.toLocaleString()}`,
          },
          {
            title: 'Items Sold',
            data: totalOrders.toLocaleString(),
          },
        ]}
        isTitleClickable={true}
        onClickTitle={() => {
          if (onDetailClick) {
            onDetailClick({ products: data, sortBy, loading })
          } else {
            setComponent('topProductsChart')
            setShowDetail(true)
          }
        }}
        onToggleDataClick={(index) => {
          handleSortByChange(index === 0 ? 'revenue' : 'items_sold')
        }}
        activeToggle={sortBy === 'revenue' ? 0 : 1}
      />
    </div>
  )
}

export default TopProductsChart
